import { IconArrowDown, IconBrush, IconDeviceDesktop, IconRocket, IconSparkles } from "@tabler/icons-react"
import { type FC, type ReactNode, useEffect, useState } from "react"
import { useSearchParams } from "react-router"

import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { AddIcon } from "~/components/Icon"
import Layout, { PageTitle } from "~/components/Layout"
import { usePlatform, useUser } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import AiLandingPageModal from "./AiLandingPageModal"
import LandingPageEditor from "./LandingPageEditor"
import UcraftImageSelectionModal from "./UcraftImageSelectionModal"
import UcraftMigrationBanner from "./UcraftMigrationBanner"

const header = <PageTitle>Website</PageTitle>

const WebsiteEditorPage = withAccessRequirement("landingPage", header, () => {
	const platform = usePlatform()!
	const user = useUser()
	const isImpersonating = user.user?.isImpersonating ?? false
	const [searchParams, setSearchParams] = useSearchParams()

	const [generationId, setGenerationId] = useState<number | undefined>()

	const [isAiModalOpen, setAiModalOpen] = useState(searchParams.get("ai") === "true")

	// The dashboard links here with ?ai=true. Without a site it opens the site generator and is consumed here,
	// so the editor mounting after generation does not also open its page generator. Otherwise the editor consumes it.
	const hasLandingPage = platform.landingPage !== null
	useEffect(() => {
		if (hasLandingPage || !searchParams.has("ai")) return
		setSearchParams(
			params => {
				params.delete("ai")
				return params
			},
			{ replace: true },
		)
	}, [hasLandingPage, searchParams, setSearchParams])

	const dismissMigratedNotice = query.landing.dismissMigratedNotice.useMutation({
		onSuccess: () => user.reload(),
		onError: error => console.error(error),
	})
	const createPage = query.landing.pages.create.useMutation({ onSuccess: () => user.reload() })
	const canUseLandingPageAi = platform.enabledFeatures.includes("ai-generated-courses")
	const createStarterPage = () => createPage.mutate({ path: "/", title: "Home" })
	const showLandingPageAiAction = canUseLandingPageAi || true

	const aiModal = (
		<AiLandingPageModal
			isOpen={isAiModalOpen && canUseLandingPageAi}
			defaultLanguage={platform.language}
			onClose={() => setAiModalOpen(false)}
			onGenerated={async generationId => {
				setGenerationId(generationId)
				await user.reload()
			}}
		/>
	)

	if (platform.landingPage?.type === "revive") {
		return (
			<Layout>
				{header}
				<UcraftChoice
					canManageUcraft={isImpersonating}
					hasCustomDomain={platform.domain.hasCustomDomain}
					platformName={platform.name}
					platformSlug={platform.slug}
					onSwitched={user.reload}
				/>
			</Layout>
		)
	}

	if (platform.landingPage?.type === "migrating") {
		if (isImpersonating && platform.reviveSiteSlug !== null) {
			const ucraftSiteUrl = `https://${platform.reviveSiteSlug}.forento.site`
			const newSiteUrl = `https://${platform.slug}.forento.net`

			return (
				<div className="flex h-screen flex-col overflow-hidden bg-gray-50">
					<UcraftMigrationBanner
						ucraftSiteUrl={ucraftSiteUrl}
						newSiteUrl={newSiteUrl}
						onFinished={user.reload}
					/>
					<LandingPageEditor generationId={generationId} />
				</div>
			)
		}

		return (
			<div className="flex h-screen flex-col overflow-hidden bg-gray-50">
				<LandingPageEditor generationId={generationId} />
			</div>
		)
	}

	if (platform.landingPage?.type === "landing-page") {
		if (platform.landingPage.showMigratedNotice) {
			return (
				<Layout>
					{header}
					<WebsiteLaunchIntro
						platformName={platform.name}
						platformSlug={platform.slug}
						title="Your landing page has moved"
						text={
							<>
								<p>
									We've just upgraded you to the new Forento website builder, and we're excited for
									you to see it. What you're seeing right now is just your existing content dropped in
									as a starting point. The real fun starts when you make it yours.
								</p>
								<p>
									This builder is something we're genuinely proud of. Built from the ground up using
									insights from tens of thousands of courses and websites, it's faster, cleaner, and
									designed specifically for course creators. Better SEO, better experience, better
									results. No fluff, just what works.
								</p>
								<p>
									Start exploring and see what's possible. Want to go even further? Message us to get
									invited to Forento AI, our private beta where you can build your entire site with
									AI.
								</p>
							</>
						}
						actionTitle="Continue with your upgraded site"
					>
						<Button
							variant="primary"
							onClick={() => dismissMigratedNotice.mutate()}
							isLoading={dismissMigratedNotice.isPending}
							className="min-w-56 lg:w-full"
						>
							Continue to editor
						</Button>
					</WebsiteLaunchIntro>
				</Layout>
			)
		}

		return (
			<div className="flex h-screen flex-col overflow-hidden bg-gray-50">
				<LandingPageEditor generationId={generationId} />
			</div>
		)
	}

	if (platform.ucraftSiteDeleteDate !== null) {
		return (
			<Layout>
				{aiModal}
				{header}
				<WebsiteLaunchIntro
					platformName={platform.name}
					platformSlug={platform.slug}
					title="Welcome back!"
					text={
						<>
							<p>
								You picked a great time to return, because what's waiting for you is something
								completely new.
							</p>
							<p>
								The Forento website builder has been rebuilt from scratch, and it shows. Faster loading,
								better SEO, and an experience that finally feels as good as the rest of the platform. We
								studied what works across tens of thousands of real courses and websites and built
								exactly that. No bloat, no friction, just a beautiful site for your academy in a
								fraction of the time.
							</p>
							<p>
								{showLandingPageAiAction
									? "Jump in whenever you're ready. You can use Forento AI to generate a site from a few details, or start from a ready-to-edit page built from your academy details."
									: "Jump in whenever you're ready. Start from a ready-to-edit page built from your academy details and shape it around your offer."}
							</p>
						</>
					}
					actionTitle={
						showLandingPageAiAction ? "Generate a strong first draft" : "Start with a strong first draft"
					}
				>
					<InitialWebsiteActions
						canUseAi={canUseLandingPageAi}
						isCreatingStarter={createPage.isPending}
						onUseAi={() => setAiModalOpen(true)}
						onCreateFromStarter={createStarterPage}
						className="justify-start lg:flex-col lg:items-stretch"
					/>
				</WebsiteLaunchIntro>
			</Layout>
		)
	}

	return (
		<Layout>
			{aiModal}
			{header}
			<WebsiteLaunchIntro
				platformName={platform.name}
				platformSlug={platform.slug}
				title="Build a site that already feels ready to launch."
				text={
					showLandingPageAiAction
						? "Let Forento AI generate a polished first version, or start from a page shaped around your academy details. Either way, you land inside the editor with something real to refine."
						: "Start from a ready-to-edit page shaped around your academy details, then refine the copy, sections, and offer until it feels ready to publish."
				}
				actionTitle={
					showLandingPageAiAction ? "Generate a strong first draft" : "Start with a strong first draft"
				}
			>
				<InitialWebsiteActions
					canUseAi={canUseLandingPageAi}
					isCreatingStarter={createPage.isPending}
					onUseAi={() => setAiModalOpen(true)}
					onCreateFromStarter={createStarterPage}
					className="justify-start lg:flex-col lg:items-stretch"
				/>
			</WebsiteLaunchIntro>
		</Layout>
	)
})

const launchHighlights = [
	{
		label: "Branded from the start",
		Icon: IconBrush,
		className: "border-rose-100 bg-rose-50 text-rose-700",
	},
	{
		label: "Desktop and mobile ready",
		Icon: IconDeviceDesktop,
		className: "border-sky-100 bg-sky-50 text-sky-700",
	},
	{
		label: "Built to publish fast",
		Icon: IconRocket,
		className: "border-emerald-100 bg-emerald-50 text-emerald-700",
	},
] as const

type Props = {
	platformName: string
	platformSlug: string
	title: string
	text: ReactNode
	actionTitle: string
	children: ReactNode
}
const WebsiteLaunchIntro: FC<Props> = ({ platformName, platformSlug, title, text, actionTitle, children }) => {
	return (
		<div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl shadow-slate-200/80">
			<div className="relative overflow-hidden bg-linear-to-br from-sky-50 via-white to-rose-50 px-4 pt-8 pb-16 sm:px-8 lg:px-10">
				<div className="relative mx-auto max-w-5xl">
					<WebsitePreview platformName={platformName} platformSlug={platformSlug} />
				</div>
				<div className="absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-white via-white/90 to-transparent" />
			</div>
			<div className="relative flex gap-8 px-6 pb-8 sm:px-10 lg:items-end">
				<div className="grow">
					<h2 className="m-0 text-3xl leading-tight font-semibold text-slate-950">{title}</h2>
					<div className="mt-4 max-w-2xl space-y-2 text-base leading-relaxed text-slate-600">
						{typeof text === "string" ? <p>{text}</p> : text}
					</div>
					<div className="mt-6 flex flex-wrap gap-2.5 xl:flex-nowrap">
						{launchHighlights.map(({ label, Icon, className }) => (
							<div
								key={label}
								className={cn(
									"inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold whitespace-nowrap",
									className,
								)}
							>
								<Icon className="size-4" />
								{label}
							</div>
						))}
					</div>
				</div>
				<div className="flex basis-64 flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
					<div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
						<span className="text-primary flex size-7 items-center justify-center rounded-full bg-white shadow-sm">
							<IconArrowDown className="size-4" />
						</span>
						{actionTitle}
					</div>
					{children}
				</div>
			</div>
		</div>
	)
}

type WebsitePreviewProps = { platformName: string; platformSlug: string }
const WebsitePreview: FC<WebsitePreviewProps> = ({ platformName, platformSlug }) => (
	<div className="relative mx-auto max-w-4xl">
		<div className="relative overflow-hidden rounded-lg border border-white/80 bg-white shadow-2xl ring-1 shadow-slate-300/80 ring-black/5">
			<div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
				<div className="flex gap-1.5">
					<span className="size-2.5 rounded-full bg-rose-400" />
					<span className="size-2.5 rounded-full bg-amber-400" />
					<span className="size-2.5 rounded-full bg-emerald-400" />
				</div>
				<div className="flex min-w-0 flex-1 items-center rounded-md bg-white px-3 py-1.5 text-xs text-slate-400 shadow-sm">
					{platformSlug}.forento.net
				</div>
			</div>
			<div className="bg-white">
				<header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
					<div className="flex min-w-0 items-center gap-3">
						<div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-slate-950 text-sm font-semibold text-white">
							{platformName.slice(0, 1).toUpperCase()}
						</div>
						<div className="min-w-0">
							<div className="truncate font-semibold text-slate-950">{platformName}</div>
							<div className="text-xs text-slate-400">Online academy</div>
						</div>
					</div>
					<div className="hidden items-center gap-5 text-sm text-slate-500 sm:flex">
						<span>Courses</span>
						<span>About</span>
						<span>Contact</span>
					</div>
				</header>
				<section className="grid min-h-80 gap-8 px-6 py-8 md:grid-cols-[minmax(0,1fr)_17rem] md:items-center">
					<div>
						<div className="mb-4 h-2 w-28 rounded-full bg-emerald-300" />
						<div className="space-y-2.5">
							<div className="h-8 max-w-md rounded-md bg-slate-950" />
							<div className="h-8 max-w-sm rounded-md bg-slate-950" />
							<div className="h-3 max-w-lg rounded-full bg-slate-200" />
							<div className="h-3 max-w-md rounded-full bg-slate-200" />
						</div>
						<div className="mt-6 flex flex-wrap gap-3">
							<div className="bg-primary h-11 w-36 rounded-lg" />
							<div className="h-11 w-28 rounded-lg border border-slate-200 bg-white" />
						</div>
					</div>
					<div className="overflow-hidden rounded-lg border border-slate-200 bg-linear-to-br from-slate-100 via-white to-sky-100 p-3 shadow-lg">
						<div className="aspect-4/3 rounded-md bg-linear-to-br from-rose-300 via-amber-200 to-emerald-300" />
					</div>
				</section>
				<section className="grid gap-4 border-t border-slate-100 bg-slate-50 px-6 py-6 sm:grid-cols-3">
					{["Signature course", "Member library", "Live sessions"].map((item, index) => (
						<div key={item} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
							<div
								className={cn(
									"mb-3 aspect-video rounded-md",
									index === 0 && "bg-sky-100",
									index === 1 && "bg-rose-100",
									index === 2 && "bg-emerald-100",
								)}
							/>
							<div className="mb-2 h-3 rounded-full bg-slate-800" />
							<div className="h-2 w-2/3 rounded-full bg-slate-200" />
						</div>
					))}
				</section>
			</div>
		</div>

		<div className="absolute top-20 -left-3 hidden w-48 rounded-lg border border-slate-200 bg-white/95 p-3 shadow-xl shadow-slate-200/80 backdrop-blur lg:block">
			<div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
				<span className="size-2 rounded-full bg-emerald-400" />
				Live structure
			</div>
			<div className="space-y-2">
				{["Hero", "Courses", "Testimonials", "Contact"].map((item, index) => (
					<div key={item} className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-2">
						<span
							className={cn(
								"size-2 rounded-full",
								index === 0 && "bg-rose-400",
								index === 1 && "bg-sky-400",
								index === 2 && "bg-amber-400",
								index === 3 && "bg-emerald-400",
							)}
						/>
						<span className="text-xs font-medium text-slate-600">{item}</span>
					</div>
				))}
			</div>
		</div>

		<div className="absolute -right-2 bottom-10 hidden rounded-lg border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/80 lg:block">
			<div className="flex items-center gap-3">
				<div className="flex size-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
					<IconSparkles className="size-5" />
				</div>
				<div>
					<div className="text-sm font-semibold text-slate-900">Preview generated</div>
					<div className="text-xs text-slate-500">Ready for your edits</div>
				</div>
			</div>
		</div>
	</div>
)

type InitialWebsiteActionsProps = {
	canUseAi: boolean
	isCreatingStarter: boolean
	onUseAi(): void
	onCreateFromStarter(): void
	className?: string
}
const InitialWebsiteActions: FC<InitialWebsiteActionsProps> = ({
	canUseAi,
	isCreatingStarter,
	onUseAi,
	onCreateFromStarter,
	className,
}) => (
	<div className={cn("flex flex-wrap items-center justify-center gap-3", className)}>
		{canUseAi && (
			<Button variant="primary" onClick={onUseAi} isDisabled={isCreatingStarter} className="min-w-56 lg:w-full">
				<IconSparkles className="size-4" />
				Use Forento AI
			</Button>
		)}
		<Button
			variant={canUseAi ? "secondary" : "primary"}
			onClick={onCreateFromStarter}
			isLoading={isCreatingStarter}
			isDisabled={isCreatingStarter}
			className="min-w-56 lg:w-full"
		>
			<AddIcon className="size-4" />
			Create from academy details
		</Button>
	</div>
)

type UcraftChoiceProps = {
	canManageUcraft: boolean
	hasCustomDomain: boolean
	platformName: string
	platformSlug: string
	onSwitched(): Promise<void>
}
const UcraftChoice: FC<UcraftChoiceProps> = ({
	canManageUcraft,
	hasCustomDomain,
	platformName,
	platformSlug,
	onSwitched,
}) => {
	const platform = usePlatform()!

	const [isMigrationModalOpen, setMigrationModalOpen] = useState(false)

	const switchToNewEditor = query.landing.switchFromUcraftToLandingPage.useMutation({
		onSuccess: onSwitched,
		onError: error => console.error(error),
	})

	return (
		<>
			<UcraftImageSelectionModal isOpen={isMigrationModalOpen} onClose={() => setMigrationModalOpen(false)} />
			<WebsiteLaunchIntro
				platformName={platformName}
				platformSlug={platformSlug}
				title="Choose how to continue with your website"
				text={
					<>
						<p>
							We recently launched a brand new Forento website builder, and we wanted to make sure you
							knew where things stand with your site. Your original site is still{" "}
							<Button onClick={`//${platform.domain.landingPage ?? ""}`} newTab variant="link">
								here
							</Button>
							, and you can reconnect your domain to keep it live exactly as you left it.
						</p>
						<p>
							But here's the thing: the new Forento website builder is a completely different experience,
							and creators are loving it. Faster, smarter, built specifically for course creators based on
							what we know actually converts. If you've ever wanted to refresh your academy or build
							something that really performs, now is the perfect time.
						</p>
						<p>
							Your old site can't be edited going forward, so whenever the moment feels right, the new
							builder is ready for you. We'd love to see what you create.
						</p>
						{hasCustomDomain && (
							<p>
								Whichever option you choose, update your DNS records because the new landing page editor
								uses different records than your old website.
							</p>
						)}
					</>
				}
				actionTitle="Move into the new builder"
			>
				<div className="flex flex-wrap items-center gap-3 lg:flex-col lg:items-stretch">
					<Button
						variant="primary"
						onClick={() => switchToNewEditor.mutate()}
						isLoading={switchToNewEditor.isPending}
						className="min-w-56 lg:w-full"
					>
						Switch to new editor
					</Button>
					{hasCustomDomain && (
						<Button
							variant="secondary"
							onClick={routes.settings.domain.index()}
							className="min-w-56 lg:w-full"
						>
							Update DNS records
						</Button>
					)}
					{canManageUcraft && (
						<Button
							variant="secondary"
							onClick={() => setMigrationModalOpen(true)}
							className="min-w-56 lg:w-full"
						>
							Migrate with AI
						</Button>
					)}
				</div>
			</WebsiteLaunchIntro>
		</>
	)
}

export default WebsiteEditorPage
